import { FC } from "react";
import NcModal from "shared/NcModal/NcModal";
import Label from "components/Label";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import moment from "moment";
import Badge from "shared/Badge/Badge";
import useCheckout from "hooks/useCheckout";
import ModalSelectDate from "components/ModalSelectDate";
import { useTranslation } from "react-i18next";
import { convertHoursToDays } from "utils/convertHoursToDisplayString";
import { Checkbox, FormControlLabel, FormGroup, Grid } from "@mui/material";
import { errorAlert } from "utils/alertHandler";
import TermsAndCondition from "components/TermsAndCondition";
import VoucherList from "components/VoucherList";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import ButtonThird from "shared/Button/ButtonThird";

export interface CheckOutPagePageMainProps {
  className?: string;
}

const CheckoutPage: FC<CheckOutPagePageMainProps> = ({ className = "" }) => {
  const {
    user,
    totalPrice,
    totalHours,
    startTime,
    endTime,
    isCreatingBooking,
    points,
    pointUsed,
    pointInfo,
    userAccountPointBalance,
    isRedeemPointLoading,
    email,
    phone,
    instagram_id,
    remarks,
    isConfirmTerms,
    isOpenTerms,
    voucher,
    discountedPrice,
    isProfileAvailableToCheckout,
    _onEmailChange,
    _onPhoneChange,
    _onRemarksChange,
    setPoints,
    _onRedeemPoint,
    _onCreate,
    _onConfirmTerms,
    _isOpenTerms,
    _onSelectVoucher,
    _onClearVoucher,
  } = useCheckout();

  const { t } = useTranslation();

  const renderMain = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
        <h2 className="text-3xl lg:text-4xl font-semibold">{t("checkout_title")}</h2>
        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
        <div>
          <div>
            <h3 className="text-2xl font-semibold">{t("checkout_reservation_information")}</h3>
            <NcModal
              renderTrigger={(openModal) => <span className="block lg:hidden  mt-1 cursor-pointer">{t("checkout_reservation_information_desc")}</span>}
              renderContent={() => <></>}
              modalTitle="Booking details"
            />
          </div>
          <div className="mt-6 border border-neutral-200 dark:border-neutral-700 rounded-3xl flex flex-col sm:flex-row divide-y sm:divide-x sm:divide-y-0 divide-neutral-200 dark:divide-neutral-700 overflow-hidden z-10">
            <ModalSelectDate
              renderChildren={({ openModal }) => (
                <button className="text-left flex-1 p-5 flex justify-between space-x-5 hover:bg-neutral-50 dark:hover:bg-neutral-800" type="button">
                  <div className="flex flex-col">
                    <span className="text-sm text-neutral-400">{t("startTime")}</span>
                    <span className="mt-1.5 text-lg text-neutral-600">{startTime ? moment(startTime).format("YYYY/MM/DD HH:mm") : "--"}</span>
                  </div>
                </button>
              )}
            />
            <ModalSelectDate
              renderChildren={({ openModal }) => (
                <button className="text-left flex-1 p-5 flex justify-between space-x-5 hover:bg-neutral-50 dark:hover:bg-neutral-800" type="button">
                  <div className="flex flex-col">
                    <span className="text-sm text-neutral-400">{t("endTime")}</span>
                    <span className="mt-1.5 text-lg text-neutral-600"> {endTime ? moment(endTime).format("YYYY/MM/DD HH:mm") : "--"}</span>
                  </div>
                </button>
              )}
            />
            <ModalSelectDate
              renderChildren={({ openModal }) => (
                <button className="text-left flex-1 p-5 flex justify-between space-x-5 hover:bg-neutral-50 dark:hover:bg-neutral-800" type="button">
                  <div className="flex flex-col">
                    <span className="text-sm text-neutral-400">{t("totalTime")}</span>
                    <span className="mt-1.5 text-lg text-neutral-600">{totalHours ? convertHoursToDays(totalHours) : "--"}</span>
                  </div>
                </button>
              )}
            />
            <ModalSelectDate
              renderChildren={({ openModal }) => (
                <button className="text-left flex-1 p-5 flex justify-between space-x-5 hover:bg-neutral-50 dark:hover:bg-neutral-800" type="button">
                  <div className="flex flex-col">
                    <span className="text-sm text-neutral-400">{t("total")}</span>
                    {discountedPrice !== totalPrice ? (
                      <>
                        <span className="mt-1.5 text-sm text-neutral-300 line-through">{totalPrice ? `HKD $${totalPrice}` : "--"}</span>
                        <span className="mt-1.5 text-lg text-primary-500">{`HKD $${discountedPrice}`}</span>
                        <span>{pointUsed && pointInfo ? <Badge color="red" name={`${t("checkout_point_redeem_badge")} ${pointUsed} ${pointInfo!["point_name"]}`} /> : <></>} </span>
                      </>
                    ) : (
                      <span className="mt-1.5 text-lg text-neutral-600">{totalPrice ? `HKD $${totalPrice}` : "--"}</span>
                    )}
                  </div>
                </button>
              )}
            />
          </div>
        </div>

        {pointInfo && (
          <>
            <h3 className="text-2xl font-semibold">{t("checkout_point_redeem_title")} 及 優惠劵</h3>
            <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 my-5"></div>
            <span className="text-sm text-neutral-600">
              {t("checkout_point_redeem_desc")} {pointInfo!["unit_price"]} {pointInfo?.["point_name"]} = 1 HKD <br />* 每次使用須為 {pointInfo!["unit_price"]} 倍數
            </span>
            <div className="space-y-2">
              <Label>
                {t("checkout_point_redeem_label")} ： {userAccountPointBalance} {pointInfo?.["point_name"]}
                <br /> 帳戶可用金額 : HKD {Number(userAccountPointBalance) > 0 ? Math.floor(Number(userAccountPointBalance) / pointInfo!["unit_price"]) : 0}
              </Label>
              <br />
              <div className="flex">
                <Grid container alignItems="center">
                  <Label>HKD $ </Label>
                  <Grid item style={{ marginLeft: 5, marginRight: 5 }}>
                    <Input
                      style={{ height: "100%" }}
                      sizeClass="mr-2"
                      type="number"
                      min={0}
                      max={Number(userAccountPointBalance) > 0 ? Math.floor(Number(userAccountPointBalance) / pointInfo!["unit_price"]) : 0}
                      value={String(points / pointInfo!["unit_price"])}
                      onChange={(e) => {
                        const max = Math.floor(Number(userAccountPointBalance) / pointInfo!["unit_price"]);

                        if (Number(e.target.value) > max) {
                          errorAlert("Excess max.");
                          return;
                        }

                        setPoints(Number(e.target.value) * pointInfo!["unit_price"]);
                      }}></Input>
                  </Grid>
                  <Grid item>
                    <ButtonPrimary sizeClass="w-[100%] px-5 sm:px-5 py-2" onClick={_onRedeemPoint} loading={isRedeemPointLoading}>
                      {t("checkout_point_redeem_btn")}
                    </ButtonPrimary>
                  </Grid>
                </Grid>
              </div>

              <div className="flex items-center" style={{ marginTop: 20 }}>
                <Grid container alignItems="center">
                  <Grid item style={{ marginRight: 20 }}>
                    {voucher ? (
                      <>
                        <span className="text-sm text-neutral-600 ">已選擇 : </span>
                        <Badge
                          name={
                            <div className="flex items-center" style={{ height: 30 }}>
                              <span className="ml-1">{voucher?.["voucher"]?.["voucher_name"]}</span>
                              <ButtonClose onClick={_onClearVoucher} />
                            </div>
                          }
                          color="yellow"
                        />
                        <span className="text-sm text-neutral-600 "> ( {voucher?.["appliedCount"]} 張 ) </span>
                      </>
                    ) : (
                      <span className="text-sm text-neutral-600 ">尚未使用優惠劵*</span>
                    )}
                  </Grid>
                  <Grid item>
                    <VoucherList isUsedIcon={false} title={"選擇優惠劵"} action={_onSelectVoucher} />
                  </Grid>
                </Grid>
              </div>
            </div>
          </>
        )}

        <div>
          <h3 className="text-2xl font-semibold">{t("checkout_contact_title")}</h3>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 my-5"></div>
          <div className="mt-6">
            <div className="space-y-1">
              <Label>{t("checkout_contact_email")}</Label>
              <Input
                type="email"
                defaultValue={user!["email"]}
                value={String(email || "")}
                onChange={(e) => {
                  _onEmailChange(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("checkout_contact_phone")}</Label>
              <Input
                type="number"
                defaultValue={user!["phone"]}
                value={String(phone)}
                onChange={(e) => {
                  _onPhoneChange(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("checkout_contact_remarks")}</Label>
              <Textarea
                value={String(remarks || "")}
                onChange={(e) => {
                  _onRemarksChange(e.target.value);
                }}
              />
              <span className="text-sm text-neutral-500 block"></span>
            </div>

            <FormGroup sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={isConfirmTerms}
                    sx={{
                      color: "grey",
                      "&.Mui-checked": {
                        color: "grey",
                      },
                    }}
                    onChange={(e) => {
                      _onConfirmTerms();
                    }}
                  />
                }
                label={
                  <span className="text-sm text-neutral-600">
                    {t("checkout_checkbox_desc") + " "}
                    <a style={{ textDecoration: "underline" }} className="text-sm text-neutral-600" onClick={_isOpenTerms}>
                      {t("all_terms")}
                    </a>
                  </span>
                }
              />
            </FormGroup>
            <TermsAndCondition isShowModal={isOpenTerms} _onClose={_isOpenTerms} />
            <div className="pt-8">
              {!isProfileAvailableToCheckout ? (
                <>
                  <span className="text-sm text-neutral-500 block mb-5" aria-required>
                    * {t("checkout_personal_info_required")}
                  </span>
                  <ButtonThird href={"/my-profile"}>{t("checkout_personal_info_required_btn_title")}</ButtonThird>
                </>
              ) : (
                <ButtonPrimary loading={isCreatingBooking} onClick={_onCreate}>
                  {!isCreatingBooking && t("checkout_confirm_btn")}
                </ButtonPrimary>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`nc-CheckOutPagePageMain ${className}`}>
      <main className="container mt-11 mb-24 lg:mb-32 flex flex-col-reverse lg:flex-row">
        <div className="w-full lg:w-3/5 xl:w-2/3 lg:pr-10 ">{renderMain()}</div>
        {/* <div className="hidden lg:block flex-grow">{renderSidebar()}</div> */}
      </main>
    </div>
  );
};

export default CheckoutPage;
